import { html, render } from "lit-html";
import { loadMapLibre } from "@escapio/portal/components/map/map-libre/load";
import { getFormatter } from "@escapio/shared/l10n/currency.js";
import { travelData } from "@escapio/portal/state/travelData.js";

/**
 * @typedef {import('maplibre-gl').Marker} Marker
 * @return {Promise<Marker>}
 */
export const createMarker = async (...args) =>
	new (await getMarkerClass())(...args);

let MarkerClass = null;
async function getMarkerClass() {
	const { Marker: MapLibreMarker } = await loadMapLibre();

	MarkerClass =
		MarkerClass ||
		class extends MapLibreMarker {
			/**
			 * @typedef {import('maplibre-gl').MarkerOptions} MarkerOptions
			 * @param {MarkerOptions} options
			 */
			constructor(options) {
				const element = document.createElement("div");
				super({ element, anchor: "bottom", ...options });
				this.isActive = false;
				this.isSecondary = false;
				this.isVisited = false;
				this.loading = true;

				this.renderContent();
			}

			renderContent() {
				const template = this.loading
					? html`<span class="map-marker__loader">
							<i class="fa fa-circle animated-trio-loader-element"></i>
							<i class="fa fa-circle animated-trio-loader-element"></i>
							<i class="fa fa-circle animated-trio-loader-element"></i>
						</span>`
					: this.price
						? html`<span class="map-marker__price">${this.price}</span>`
						: html`<span class="map-marker__bed"><i class="fa fa-bed"></span>`;
				render(template, this.getElement());
			}

			/**
			 * Set marker to "active" state
			 */
			setActive(isActive) {
				this.isActive = Boolean(isActive);
				this.updateClasses();
				return this;
			}

			/**
			 * Set marker to "visited" state
			 */
			setVisited() {
				this.isVisited = true;
				this.updateClasses();
				return this;
			}

			setSecondary(isSecondary) {
				this.isSecondary = Boolean(isSecondary);
				this.updateClasses();
				return this;
			}

			setPrice(prices) {
				const bestPrice = prices?.bestPrice;
				this.price = bestPrice
					? getFormatter(travelData.currency)(bestPrice)
					: null;
				this.loading = prices?.isLoading;

				this.updateClasses();
				this.renderContent();
			}

			updateClasses() {
				const element = this.getElement();
				element.classList.add("map-marker");
				element.classList.toggle("map-marker--active", this.isActive);
				element.classList.toggle("map-marker--visited", this.isVisited);
				element.classList.toggle("map-marker--secondary", this.isSecondary);
			}

			addEventListener(type, listener) {
				this.getElement().addEventListener(type, listener);
			}

			removeEventListener(type, listener) {
				this.getElement().removeEventListener(type, listener);
			}
		};

	return MarkerClass;
}
